import { NotificationBell } from './notifications/NotificationCenter';
import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle2, Download, FileDown, Loader2, X, XCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { MARKETING_VIEW_PAGES, viewPage } from '../lib/marketingViews';
import { useAppStore } from '../store/useAppStore';
import { useIsMobile } from '../hooks/useIsMobile';
import { findNavLabelByPath } from '../config/navigation';
import { useDownloadCenterStore, type DownloadTask } from '../store/useDownloadCenterStore';
import Pagination from './Pagination';

const brandNames: Record<string, string> = {
  zeelool: 'Zeelool',
  vooglam: 'Vooglam',
};

function formatSiteCode(site: string): string {
  const raw = String(site || '').trim();
  if (!raw) return '';
  const code = raw.split('-')[0] || raw;
  return code.toUpperCase();
}

function formatTaskTime(ts?: number): string {
  if (!ts) return '';
  const date = new Date(ts);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function taskStatusText(task: DownloadTask): string {
	if (task.status === 'queued') return '排队中';
	if (task.status === 'processing') return '生成中';
  if (task.status === 'failed') return '失败';
  return task.downloaded ? '已下载' : '可下载';
}


interface TopbarProps {
  onMenuToggle?: () => void;
}

export const Topbar: React.FC<TopbarProps> = ({ onMenuToggle }) => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { brand, site, brands, brandId, sites, siteId } = useAppStore();
  const [showDownloads, setShowDownloads] = useState(false);
  const downloadTasks = useDownloadCenterStore(state => state.tasks);
  const downloadPage = useDownloadCenterStore(state => state.page);
  const downloadPageSize = useDownloadCenterStore(state => state.pageSize);
  const downloadTotal = useDownloadCenterStore(state => state.total);
  const downloadLoading = useDownloadCenterStore(state => state.loading);
  const downloadUnseenCount = useDownloadCenterStore(state => state.unseenCount);
  const autoDownloadTaskIDs = useDownloadCenterStore(state => state.autoDownloadTaskIDs);
  const fetchDownloadTasks = useDownloadCenterStore(state => state.fetchTasks);
  const setDownloadPage = useDownloadCenterStore(state => state.setPage);
  const markDownloadTasksSeen = useDownloadCenterStore(state => state.markVisibleTasksSeen);
  const downloadTask = useDownloadCenterStore(state => state.downloadTask);
  const removeDownloadTask = useDownloadCenterStore(state => state.removeTask);
  const clearFinishedDownloads = useDownloadCenterStore(state => state.clearFinished);
  const refreshDownloadTasks = useDownloadCenterStore(state => state.refreshTasks);

  const downloadsRef = useRef<HTMLDivElement>(null);
  const autoDownloadingTaskIDs = useRef<Set<string>>(new Set());

  const pageTitle = findNavLabelByPath(location.pathname) ?? '鹿鸣·广告投放平台';

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadsRef.current && !downloadsRef.current.contains(event.target as Node)) {
        setShowDownloads(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleDownloads = () => {
    const nextOpen = !showDownloads;
    setShowDownloads(nextOpen);
    if (nextOpen) {
      void fetchDownloadTasks().then(() => markDownloadTasksSeen());
    }
  };

  const activeDownloadCount = downloadTasks.filter(task => task.status === 'queued' || task.status === 'processing').length;
  const hasFinishedDownloadTasks = downloadTasks.some(task => task.status === 'ready' || task.status === 'failed');
  const downloadTotalPages = Math.max(1, Math.ceil(downloadTotal / downloadPageSize));

  useEffect(() => {
    if (brandId) {
      void refreshDownloadTasks(brandId);
    }
  }, [brandId, refreshDownloadTasks]);

  useEffect(() => {
    if (showDownloads && brandId) {
      void refreshDownloadTasks(brandId);
    }
  }, [brandId, refreshDownloadTasks, showDownloads]);

  useEffect(() => {
    if (!brandId || activeDownloadCount === 0) return;
    const timer = window.setInterval(() => {
      void refreshDownloadTasks(brandId);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [activeDownloadCount, brandId, refreshDownloadTasks]);

  useEffect(() => {
    const autoDownloadReadyTasks = downloadTasks.filter(task =>
      task.status === 'ready' && !task.downloaded && autoDownloadTaskIDs.includes(task.id),
    );
    for (const task of autoDownloadReadyTasks) {
      if (autoDownloadingTaskIDs.current.has(task.id)) continue;
      autoDownloadingTaskIDs.current.add(task.id);
      void downloadTask(task.id).finally(() => {
        autoDownloadingTaskIDs.current.delete(task.id);
      });
    }
  }, [autoDownloadTaskIDs, downloadTask, downloadTasks]);

  return (
    <header className="topbar">
      {/* 移动端汉堡菜单 */}
      <button className="menu-toggle" onClick={onMenuToggle} aria-label="打开菜单">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
          <line x1="3" y1="6" x2="21" y2="6"/>
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <span className="bc-brand">{brands.find(b => b.id === brandId)?.name ?? brandNames[brand] ?? brand}</span>
        <span className="bc-sep">/</span>
        <span className="bc-site">{formatSiteCode(sites.find(s => s.id === siteId)?.code ?? site)}</span>
        <span className="bc-sep">/</span>
      </div>

      {/* Page Title */}
      <span className="page-title">{pageTitle}</span>

      {/* Right Section */}
      <div className="topbar-right">
        {/* Download Center */}
        <div ref={downloadsRef} style={{ position: 'relative' }}>
          <div
            className={`icon-btn download-center-btn ${activeDownloadCount > 0 ? 'is-processing' : downloadUnseenCount > 0 ? 'badge-dot' : ''}`}
            onClick={handleToggleDownloads}
            style={{ cursor: 'pointer' }}
            title="下载中心"
          >
            {activeDownloadCount > 0 ? (
              <span className="download-center-processing-icon" aria-hidden="true">
                <span className="download-center-box">
                  <span className="download-center-arrow" />
                </span>
              </span>
            ) : (
              <FileDown size={16} strokeWidth={2} />
            )}
          </div>

          {showDownloads && (
            <div
              style={{
                position: isMobile ? 'fixed' : 'absolute',
                top: isMobile ? 56 : 'calc(100% + 8px)',
                right: isMobile ? 8 : 0,
                width: isMobile ? 'calc(100vw - 16px)' : '420px',
                background: 'var(--surface)',
                border: '1px solid var(--border2)',
                borderRadius: 'var(--radius)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.18)',
                zIndex: 100,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 16px',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: '13px' }}>下载中心</span>
                <button
                  onClick={clearFinishedDownloads}
                  disabled={!hasFinishedDownloadTasks}
                  style={{
                    fontSize: '11px',
                    color: 'var(--primary-light)',
                    background: 'none',
                    border: 'none',
                    cursor: hasFinishedDownloadTasks ? 'pointer' : 'not-allowed',
                    opacity: hasFinishedDownloadTasks ? 1 : 0.45,
                  }}
                >
                  清理完成项
                </button>
              </div>
              <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                {downloadLoading && downloadTasks.length === 0 ? (
                  <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>
                    加载中...
                  </div>
                ) : downloadTasks.length === 0 ? (
                  <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>
                    暂无导出任务
                  </div>
                ) : downloadTasks.map(task => (
                  <div
                    key={task.id}
                    style={{
                      display: 'flex',
                      gap: 12,
                      padding: '12px 16px',
                      borderBottom: '1px solid var(--border)',
                      alignItems: 'flex-start',
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        background: task.status === 'failed' ? 'var(--red-bg)' : task.status === 'ready' ? 'var(--green-bg)' : 'var(--primary-bg)',
                        color: task.status === 'failed' ? 'var(--red)' : task.status === 'ready' ? 'var(--green)' : 'var(--primary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {task.status === 'queued' || task.status === 'processing' ? <Loader2 size={15} className="spin-icon" /> : task.status === 'failed' ? <XCircle size={15} /> : <CheckCircle2 size={15} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, paddingRight: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {task.title}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {task.fileName}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 11, color: 'var(--text3)' }}>
                        <span>{taskStatusText(task)}</span>
                        <span>{formatTaskTime(task.completedAt ?? task.createdAt)}</span>
                      </div>
                      {task.error && (
                        <div
                          title={task.error}
                          style={{ marginTop: 4, color: 'var(--red)', fontSize: 11, lineHeight: '16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {task.status === 'failed' ? task.error : `下载失败：${task.error}`}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={task.status !== 'ready'}
                        onClick={() => downloadTask(task.id)}
                        title="下载"
                        aria-label="下载"
                        style={{ width: 28, height: 28, padding: 0, justifyContent: 'center' }}
                      >
                        <Download size={15} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={task.status === 'queued' || task.status === 'processing'}
                        onClick={() => removeDownloadTask(task.id)}
                        title="删除"
                        aria-label="删除"
                        style={{ width: 28, height: 28, padding: 0, justifyContent: 'center' }}
                      >
                        <X size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <Pagination
                className="pagination-compact"
                page={downloadPage}
                totalPages={downloadTotalPages}
                onChange={nextPage => void setDownloadPage(nextPage).then(() => markDownloadTasksSeen())}
                total={`共 ${downloadTotal} 条`}
                disabled={downloadLoading}
                style={{ padding: '10px 16px', borderTop: '1px solid var(--border)' }}
              />
            </div>
          )}
        </div>

        <NotificationBell downloadsOpen={showDownloads} onOpen={() => setShowDownloads(false)} />
        <span id="marketing-saved-views-slot" hidden={!MARKETING_VIEW_PAGES.has(viewPage(location.pathname))} />
      </div>
    </header>
  );
};

export default Topbar;
